import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAutomationRules } from "../../../../../../../store/contexts/AutomationRulesContext";
import MainApi from "../../../../../../../services/mainApi.service";
import { IAutomationTemplateProps } from "./model";
import {
  automationTemplateSchema,
  TAutomationTemplateForm,
} from "./automationTemplateSchema";
import {
  createTicketAutomationMsgTicketAutTemplate,
  getTicketAutomationMsgTicketAutTemplateById,
  updateTicketAutomationMsgTicketAutTemplateById,
} from "../../../../../../../services/automationMessageTicketAutomationTemplate.service";
import { SchedulesField } from "./SchedulesField";
import { InsurancesField } from "./InsurancesField";

const translationPath =
  "chatBot.page.automations.ticketAutomationMsgTicketAutTemplate.";

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";

const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const TICKET_TEMPLATE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationTemplate/";

const ticketTemplateService = new MainApi<ISelectType>(TICKET_TEMPLATE_ROUTE);

export const AutomationTemplate = memo(
  ({ id, ticketAutomationRuleId, onBackButton }: IAutomationTemplateProps) => {
    const { t } = useTranslation();

    const { automation } = useAutomationRules();

    const [options, setOptions] = useState<ISelectType[]>([]);

    const { sendRequest } = useFetch(genericService.list);

    const fetchGenerics = useCallback(async () => {
      if (!automation) return;
      const { data, success } = await sendRequest({
        Identifier: "TicketAutomationMessageProtocolSendWhen",
      });

      let filtered: string[] = [];
      if (automation.code === "7") {
        filtered = ["4"];
      } else if (["1", "5", "11"].includes(String(automation.code))) {
        filtered = ["1", "2", "3", "5", "6"];
      } else if (
        ["12", "13", "14", "16", "17", "18"].includes(String(automation.code))
      ) {
        filtered = ["1", "3", "5", "6"];
      } else {
        filtered = ["1", "2", "3"];
      }

      if (data && success) {
        setOptions(
          data
            .filter((d) => filtered.includes(String(d.code)))
            .map((d) => ({
              id: d.id,
              name: d.name,
            })),
        );
      }
    }, [sendRequest, automation]);

    useEffect(() => {
      fetchGenerics();
    }, [fetchGenerics]);

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutomationMsgTicketAutTemplate,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutomationMsgTicketAutTemplateById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutomationMsgTicketAutTemplateById,
    );

    const updateHandler = (v: TAutomationTemplateForm, id: string) =>
      update({
        item: {
          id: id,
          ticketAutomationRuleId: ticketAutomationRuleId ?? "",
          schedules: v.schedules ? v.schedules : undefined,
          insurances: v.insurances ? v.insurances : undefined,
          sendWhenId: v.sendWhen.id,
          ticketAutomationTemplateId: v.ticketAutomationTemplate.id,
        },
        id: id,
      });

    const saveHandler = (v: TAutomationTemplateForm) =>
      save({
        ticketAutomationRuleId: ticketAutomationRuleId ?? "",
        schedules: v.schedules ? v.schedules : undefined,
        insurances: v.insurances ? v.insurances : undefined,
        sendWhenId: v.sendWhen.id,
        ticketAutomationTemplateId: v.ticketAutomationTemplate.id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TAutomationTemplateForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TAutomationTemplateForm = {
            ...data,
            ticketAutomationTemplate: data.ticketAutomationTemplate,
            schedules: data.schedules,
            insurances: data.insurances,
            sendWhen: data.sendWhen,
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );
    const successHandle = () => {
      onBackButton();
    };
    const loading = sLoading || uLoading || gLoading;
    return (
      <FormContainer<TAutomationTemplateForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "title")}
        schema={automationTemplateSchema}
        subtitle={t(translationPath + "new")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={successHandle}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AutoComplete
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "send_when")}
                name="sendWhen"
                options={options}
                clearIcon={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AutoComplete
                params={{ AutomationCode: "19" }}
                getOptionLabel={({ name }) => name}
                size="small"
                label={t(translationPath + "ticket_automation_template")}
                name="ticketAutomationTemplate"
                clearIcon={false}
                request={ticketTemplateService.list}
              />
            </Grid>
            <SchedulesField />
            <InsurancesField />
          </Grid>
        )}
      />
    );
  },
);
